import { useRef, useMemo, useState, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { RoundedBox, Text, Float, Environment, Stars } from "@react-three/drei";
import * as THREE from "three";

const CARD_NAMES = [
  { name: "THE SUN", numeral: "XIX" },
  { name: "THE STAR", numeral: "XVII" },
  { name: "WHEEL OF\nFORTUNE", numeral: "X" },
  { name: "THE EMPRESS", numeral: "III" },
  { name: "THE MAGICIAN", numeral: "I" },
  { name: "THE WORLD", numeral: "XXI" },
  { name: "THE LOVERS", numeral: "VI" },
];

function fanSlot(i, n) {
  const spread = 5.4;
  const x = (i - (n - 1) / 2) * (spread / (n - 1)) * 1.6;
  const y = -Math.abs(i - (n - 1) / 2) * 0.28;
  const rz = -(i - (n - 1) / 2) * 0.14;
  return { pos: new THREE.Vector3(x, y, i * 0.06), rotZ: rz };
}

function TarotCard({ slotIndex, order, card }) {
  const ref = useRef();
  const target = useMemo(() => fanSlot(order, CARD_NAMES.length), [order]);

  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime;
    ref.current.position.lerp(
      new THREE.Vector3(target.pos.x, target.pos.y + Math.sin(t * 0.8 + slotIndex) * 0.08, target.pos.z),
      0.08
    );
    ref.current.rotation.z = THREE.MathUtils.lerp(ref.current.rotation.z, target.rotZ, 0.08);
    ref.current.rotation.y = THREE.MathUtils.lerp(ref.current.rotation.y, state.pointer.x * 0.25, 0.05);
  });

  return (
    <group ref={ref} position={[0, 3.5, slotIndex * 0.06]}>
      <RoundedBox args={[1.5, 2.5, 0.03]} radius={0.07} smoothness={4}>
        <meshStandardMaterial color="#0d1023" metalness={0.75} roughness={0.3} />
      </RoundedBox>
      <RoundedBox args={[1.38, 2.38, 0.032]} radius={0.06} smoothness={4} position={[0, 0, 0.004]}>
        <meshStandardMaterial color="#111631" metalness={0.5} roughness={0.45} />
      </RoundedBox>
      <mesh position={[0, 0.28, 0.03]}>
        <torusGeometry args={[0.34, 0.012, 12, 48]} />
        <meshStandardMaterial color="#D4AF37" metalness={1} roughness={0.25} emissive="#8a6d1f" emissiveIntensity={0.4} />
      </mesh>
      <mesh position={[0, 0.28, 0.03]} rotation={[0, 0, Math.PI / 4]}>
        <torusGeometry args={[0.24, 0.008, 8, 4]} />
        <meshStandardMaterial color="#D4AF37" metalness={1} roughness={0.3} />
      </mesh>
      <Text position={[0, 1.05, 0.03]} fontSize={0.14} color="#D4AF37" anchorX="center" anchorY="middle" letterSpacing={0.15}>
        {card.numeral}
      </Text>
      <Text position={[0, -0.72, 0.03]} fontSize={0.115} color="#e8d896" anchorX="center" anchorY="middle" letterSpacing={0.2} textAlign="center" maxWidth={1.2}>
        {card.name}
      </Text>
      <mesh position={[0, -1, 0.03]}>
        <planeGeometry args={[0.7, 0.006]} />
        <meshBasicMaterial color="#D4AF37" transparent opacity={0.7} />
      </mesh>
    </group>
  );
}

function CardFan() {
  const [orders, setOrders] = useState(() => CARD_NAMES.map((_, i) => i));

  useEffect(() => {
    const id = setInterval(() => {
      setOrders((prev) => {
        const next = [...prev];
        for (let i = next.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [next[i], next[j]] = [next[j], next[i]];
        }
        return next;
      });
    }, 3800);
    return () => clearInterval(id);
  }, []);

  return (
    <Float speed={1.2} rotationIntensity={0.08} floatIntensity={0.25}>
      <group position={[1.6, 0, 0]} scale={0.92}>
        {CARD_NAMES.map((card, i) => (
          <TarotCard key={card.name} slotIndex={i} order={orders[i]} card={card} />
        ))}
      </group>
    </Float>
  );
}

function NebulaClouds() {
  const tex = useMemo(() => {
    const c = document.createElement("canvas");
    c.width = c.height = 256;
    const ctx = c.getContext("2d");
    const g = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
    g.addColorStop(0, "rgba(255,255,255,1)");
    g.addColorStop(0.4, "rgba(255,255,255,0.45)");
    g.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 256, 256);
    return new THREE.CanvasTexture(c);
  }, []);

  const clouds = useMemo(() => [
    { pos: [-7, 2.5, -10], scale: 15, color: "#5b3fa8", opacity: 0.17 },
    { pos: [7, -2.5, -12], scale: 17, color: "#2c4f8f", opacity: 0.15 },
    { pos: [2, 4.5, -14], scale: 19, color: "#7a2f63", opacity: 0.12 },
    { pos: [-3, -4.5, -11], scale: 13, color: "#274f6d", opacity: 0.13 },
    { pos: [11, 3, -16], scale: 21, color: "#43307f", opacity: 0.1 },
  ], []);

  const ref = useRef();
  useFrame((state) => {
    if (ref.current) {
      ref.current.rotation.z = Math.sin(state.clock.elapsedTime * 0.02) * 0.06;
      ref.current.position.x = Math.sin(state.clock.elapsedTime * 0.05) * 0.5;
    }
  });

  return (
    <group ref={ref}>
      {clouds.map((c, i) => (
        <sprite key={i} position={c.pos} scale={[c.scale, c.scale * 0.65, 1]}>
          <spriteMaterial map={tex} color={c.color} transparent opacity={c.opacity} depthWrite={false} blending={THREE.AdditiveBlending} />
        </sprite>
      ))}
    </group>
  );
}

function Constellations() {
  const groups = useMemo(() => {
    const make = (cx, cy, n) => {
      const pts = [];
      let x = cx, y = cy;
      for (let i = 0; i < n; i++) {
        pts.push(new THREE.Vector3(x, y, 0));
        x += (Math.random() - 0.38) * 1.7;
        y += (Math.random() - 0.5) * 1.5;
      }
      return { pts, geo: new THREE.BufferGeometry().setFromPoints(pts) };
    };
    return [make(-8, 3.2, 6), make(5.5, 4.2, 5), make(-5, -3.8, 5), make(8.5, -2.8, 6)];
  }, []);

  const mats = useRef([]);
  useFrame((state) => {
    mats.current.forEach((m, i) => {
      if (m) m.opacity = 0.24 + Math.sin(state.clock.elapsedTime * 0.7 + i * 2.1) * 0.14;
    });
  });

  return (
    <group position={[0, 0, -9]}>
      {groups.map(({ pts, geo }, gi) => (
        <group key={gi}>
          <line geometry={geo}>
            <lineBasicMaterial ref={(el) => (mats.current[gi] = el)} color="#cbbfff" transparent opacity={0.3} />
          </line>
          {pts.map((p, pi) => (
            <mesh key={pi} position={p}>
              <sphereGeometry args={[0.045, 8, 8]} />
              <meshBasicMaterial color="#efe6ff" />
            </mesh>
          ))}
        </group>
      ))}
    </group>
  );
}

function CameraRig() {
  useFrame((state) => {
    const scroll = Math.min((window.scrollY || 0) / 600, 1);
    state.camera.position.z = THREE.MathUtils.lerp(state.camera.position.z, 8.5 - scroll * 1.6, 0.03);
    state.camera.position.x = THREE.MathUtils.lerp(state.camera.position.x, state.pointer.x * 0.6, 0.04);
    state.camera.position.y = THREE.MathUtils.lerp(state.camera.position.y, state.pointer.y * 0.35, 0.04);
    state.camera.lookAt(0.8, 0, 0);
  });
  return null;
}

export const Hero3D = () => {
  return (
    <div className="absolute inset-0" data-testid="hero-3d-canvas">
      <Canvas camera={{ position: [0, 0, 13], fov: 42 }} dpr={[1, 1.5]} gl={{ antialias: true, alpha: true }}>
        <ambientLight intensity={0.6} />
        <directionalLight position={[4, 6, 5]} intensity={1.4} color="#fff5d6" />
        <pointLight position={[-5, -2, 3]} intensity={0.8} color="#D4AF37" />
        <pointLight position={[0, 3, -2]} intensity={0.5} color="#4f46e5" />
        <Stars radius={70} depth={45} count={5000} factor={4} saturation={0} fade speed={0.6} />
        <NebulaClouds />
        <Constellations />
        <CardFan />
        <CameraRig />
        <Environment preset="night" />
      </Canvas>
    </div>
  );
};
